import React from 'react'
import { Paper, Typography, Box, Tab, Tabs, Stack, Divider, Card, Chip, Button, CircularProgress } from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import { FormTemplate } from '@/core/models/form.model'
import { useLazyExportFormSubmissionsToCSVQuery } from '@/core/services/form'
import FormStatisticsContainer from '../containers/FormStatisticsContainer'
import FileSaver from 'file-saver'

interface ViewFormTemplateProps {
  template: FormTemplate
}

const ViewFormTemplate: React.FC<ViewFormTemplateProps> = ({ template }) => {
  const [tabValue, setTabValue] = React.useState(0)
  const [triggerExport, { isLoading: isExporting }] = useLazyExportFormSubmissionsToCSVQuery()

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  const handleDownloadCSV = async () => {
    try {
      const templateId = template._id || (template as any).id
      console.log('Using templateId:', templateId, 'Type:', typeof templateId)

      if (!templateId) {
        console.error('No template ID found - template object:', template)
        alert('Error: Template ID not found. Please refresh the page.')
        return
      }

      // Trigger the export query (cache-busting parameter ensures fresh data)
      const response = await triggerExport(templateId).unwrap()

      if (!response || !response.csvContent) {
        throw new Error('No data available for export')
      }

      // Convert CSV content to blob
      const blob = new Blob([response.csvContent], { type: 'text/csv;charset=utf-8' })

      // Save the file using FileSaver (matching network export pattern)
      FileSaver.saveAs(blob, response.fileName || 'form_submissions.csv')

      console.log('CSV download initiated successfully')
    } catch (error) {
      console.error('Failed to download CSV:', error)

      // Show more specific error message
      const errorMessage = error instanceof Error ? error.message : 'Export failed. Please try again.'

      alert(`Failed to download CSV: ${errorMessage}`)
    }
  }

  return (
    <Paper>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Details" />
          <Tab label="Statistics" />
        </Tabs>
      </Box>
      <Box sx={{ p: 3 }}>
        {tabValue === 0 && (
          <Stack spacing={3}>
            <Card sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" gutterBottom>
                    {template.title}
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {template.description}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="subtitle2">Status:</Typography>
                    <Chip label={template.isActive ? 'Active' : 'Archived'} color={template.isActive ? 'success' : 'default'} size="small" />
                  </Stack>
                </Box>
                <Button
                  variant="contained"
                  startIcon={isExporting ? <CircularProgress size={16} /> : <DownloadIcon />}
                  onClick={handleDownloadCSV}
                  disabled={isExporting}
                  sx={{ minWidth: 180 }}
                >
                  {isExporting ? 'Exporting...' : 'Download CSV'}
                </Button>
              </Box>
            </Card>

            <Card sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Fields
              </Typography>
              <Stack spacing={2} divider={<Divider />}>
                {template.fields.map((field) => (
                  <Box key={field.id}>
                    <Typography variant="subtitle1" gutterBottom>
                      {field.label}
                    </Typography>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography variant="body2" color="text.secondary">
                        Type: {field.type}
                      </Typography>
                      {field.required && <Chip label="Required" size="small" color="primary" />}
                    </Stack>
                    {field.options && field.options.length > 0 && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Options:
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                          {field.options.map((option) => (
                            <Chip key={option} label={option} size="small" variant="outlined" />
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </Box>
                ))}
              </Stack>
            </Card>
          </Stack>
        )}
        {tabValue === 1 && <FormStatisticsContainer template={template} />}
      </Box>
    </Paper>
  )
}

export default ViewFormTemplate
